import cx from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { type Location, useBlocker } from 'react-router-dom';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { isEmailRule } from '@components/formInputs/rules';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { ApiError, ApiUnauthorizedError } from '@shared/api/errors';
import { NavigationWarningModal } from '@shared/components/modal/NavigationWarningModal';
import { useDocumentTitle } from '@shared/hooks/setDocumentTitle';
import { reportAppError } from '@shared/reportAppError';
import { CONFLICT } from '@shared/statusCodes';
import { useUserContext } from 'webReservations/context/UserContext';
import typography from '~styles/typography.scss';
import { updateUserEmail } from './apiHelpers';
import styles from './EmailPage.scss';

interface EmailFormData {
  email: string;
}

export const EmailPage = () => {
  useDocumentTitle('Email | Peak Reservations');

  const { user, setUser, clearUserState } = useUserContext();
  const [isSaving, setIsSaving] = useState(false);
  const shouldProceedNavigationAfterSaveRef = useRef(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<EmailFormData>({
    mode: 'onChange',
    defaultValues: { email: user?.email ?? '' },
  });

  useEffect(() => {
    reset({ email: user?.email ?? '' });
  }, [user?.email, reset]);

  const hasUnsavedValidChanges = isDirty && isValid;

  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: Location;
        nextLocation: Location;
      }) =>
        hasUnsavedValidChanges &&
        currentLocation.pathname !== nextLocation.pathname,
      [hasUnsavedValidChanges],
    ),
  );

  const submit: SubmitHandler<EmailFormData> = async ({ email }) => {
    setIsSaving(true);
    try {
      const updated = await updateUserEmail(email);
      if (user && updated.email) {
        setUser({ ...user, email: updated.email });
      }
      reset({ email: updated.email ?? '' });
      successToast({
        message: 'Email saved. Check your inbox for a new verification email.',
      });
      if (shouldProceedNavigationAfterSaveRef.current) {
        shouldProceedNavigationAfterSaveRef.current = false;
        blocker.proceed?.();
      }
    } catch (error) {
      shouldProceedNavigationAfterSaveRef.current = false;
      if (error instanceof ApiUnauthorizedError) {
        clearUserState();
        return;
      }
      if (error instanceof ApiError && error.statusCode === CONFLICT) {
        errorToast({ message: error.message });
        return;
      }
      errorToast({
        message: 'Could not update your email. Please try again.',
      });
      reportAppError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h2 className={cx(styles.title, typography.h4)}>Email</h2>
        <p className={cx(styles.description, typography.t2)}>
          We use your email address to send booking confirmations and account
          notifications.
        </p>
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit(submit)();
          }}
        >
          <ControlledFormInput
            control={control}
            label="Email Address"
            name="email"
            placeholder="jane@example.com"
            rules={{
              required: 'Email Address is required',
              validate: { isEmailRule: isEmailRule('Email Address') },
            }}
            type="email"
          />
          <Button
            className={styles.saveButton}
            isDisabled={!isDirty || !isValid || isSaving}
            isLoading={isSaving}
            label="Save Changes"
            type="submit"
            variant={ButtonVariants.Primary}
          />
        </form>
      </div>
      <NavigationWarningModal
        isOpen={blocker.state === 'blocked'}
        modalMessage={
          <p>
            You have unsaved changes to your email. Please save your work before
            navigating to a different page.
          </p>
        }
        onClose={() => {
          if (isSaving) return;
          shouldProceedNavigationAfterSaveRef.current = false;
          blocker.reset?.();
        }}
        handleLoseChangesClick={() => {
          shouldProceedNavigationAfterSaveRef.current = false;
          blocker.proceed?.();
        }}
        handleSaveChangesClick={() => {
          shouldProceedNavigationAfterSaveRef.current = true;
          void handleSubmit(submit)();
        }}
      />
    </div>
  );
};
